import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import auth from "../Account/Auth";
import be from "../BE";

export default function BookingAcceptReject() {
  const { bookingid } = useParams();
  const location = useLocation();
  const history = useHistory();

  const action = location.pathname.startsWith("/b/accept")
    ? "accept"
    : "reject";
  const bookingId = bookingid;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [loading, setLoading] = useState(false); // confirm action loading
  const [result, setResult] = useState(null);

  const [bookingLoading, setBookingLoading] = useState(true);
  const [bookingError, setBookingError] = useState(null);
  const [booking, setBooking] = useState(null);

  const formatKeyDate = (isoString, tz) => {
    try {
      const d = new Date(isoString);
      const opts = {
        dateStyle: "full",
        timeStyle: "short",
        timeZone: tz || undefined,
      };
      return d.toLocaleString(undefined, opts);
    } catch {
      return isoString;
    }
  };

  useEffect(() => {
    setIsModalOpen(true);

    const fetchBookingDetails = async () => {
      try {
        setBookingLoading(true);
        setBookingError(null);

        await auth
          .isAuthenticated()
          .then(async () => {
            await be
              .get(
                "jubilant-booking",
                `/bookings/${bookingId}`,
                false,
                true,
                true
              )
              .then((response) => {
                if (response) {
                  setBooking(response);
                } else {
                  setBookingError("No booking details found.");
                }
              })
              .catch((error) => {
                console.error("[Booking][fetch-details] network error:", error);
                setBookingError(
                  error?.message || "Network error while loading booking."
                );
              });
          })
          .catch((error) => {
            console.error(
              "[Booking][fetch-details] authentication error:",
              error
            );
            setBookingError(error?.message || "Authentication failed.");
          });
      } catch (error) {
        console.error("[Booking][fetch-details] functional error:", error);
        setBookingError(error?.message || "Something went wrong.");
      } finally {
        setBookingLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await auth
        .isAuthenticated()
        .then(async () => {
          await be
            .get(
              "jubilant-booking",
              `/booking/${action}/${bookingId}`,
              false,
              true,
              true
            )
            .then((response) => {
              setResult({
                success: true,
                message: response.message || "Operation successful.",
              });
            })
            .catch((error) => {
              console.error(`[Booking][${action}] network error:`, error);
              setResult({
                success: false,
                message:
                  error?.response?.data?.message ||
                  "Network error during operation.",
              });
            });
        })
        .catch((error) => {
          console.error(`[Booking][${action}] authentication error:`, error);
          setResult({
            success: false,
            message: error?.message || "Authentication failed.",
          });
        });
    } catch (error) {
      console.error(`[Booking][${action}] functional error:`, error);
      setResult({
        success: false,
        message: error?.message || "Something went wrong.",
      });
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleCancel = () => {
    history.push("/");
  };

  const primaryCtaLabel = useMemo(
    () => (action === "accept" ? "Accept booking" : "Reject booking"),
    [action]
  );

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-100 dark:tw-bg-gray-900 tw-p-4">
      {isModalOpen && (
        <div className="tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-shadow-lg tw-w-full tw-max-w-lg tw-p-6">
          <div className="tw-flex tw-items-start tw-justify-between">
            <div>
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 dark:tw-text-white">
                {action === "accept"
                  ? "Accept this booking?"
                  : "Reject this booking?"}
              </h2>
              <p className="tw-mt-1 tw-text-sm tw-text-gray-600 dark:!tw-text-gray-300">
                Please review the details before you continue.
              </p>
            </div>
          </div>

          {/* Details area */}
          <div className="tw-mt-5">
            {/* Loading state */}
            {bookingLoading && (
              <div className="tw-animate-pulse">
                <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded tw-w-1/2" />
                <div className="tw-mt-3 tw-h-4 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded tw-w-2/3" />
                <div className="tw-mt-3 tw-h-16 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded" />
              </div>
            )}

            {/* Error state */}
            {!bookingLoading && bookingError && (
              <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 dark:tw-bg-red-900/30 tw-p-4">
                <p className="tw-text-sm tw-text-red-700 dark:tw-text-red-300">
                  {bookingError}
                </p>
                <div className="tw-mt-3">
                  <button
                    onClick={() => {
                      // retry fetch
                      setBookingLoading(true);
                      setBookingError(null);
                      (async () => {
                        try {
                          await auth.isAuthenticated();
                          const endpoint = `/bookings/${bookingId}`;
                          const response = await be.get(
                            "jubilant-booking",
                            endpoint,
                            false,
                            true,
                            true
                          );
                          setBooking(response);
                        } catch (err) {
                          setBookingError(
                            err?.message || "Unable to load booking details."
                          );
                        } finally {
                          setBookingLoading(false);
                        }
                      })();
                    }}
                    className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-px-3 tw-py-1.5 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Loaded details */}
            {!bookingLoading && !bookingError && booking && (
              <div className="tw-space-y-4">
                {/* Event summary header */}
                <div className="tw-flex tw-items-center tw-gap-3">
                  <span
                    className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-full"
                    style={{
                      backgroundColor: booking?.info?.color || "#6f42c1",
                    }}
                    aria-hidden="true"
                  />
                  <div>
                    <div className="tw-text-base tw-font-medium tw-text-gray-900 dark:tw-text-white">
                      {booking?.info?.name || "Event"}
                    </div>
                    <div className="tw-text-sm tw-text-gray-600 dark:!tw-text-gray-300">
                      {booking?.info?.duration
                        ? `${booking.info.duration} min`
                        : null}
                    </div>
                  </div>
                </div>

                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                  <div className="tw-rounded-xl tw-bg-gray-50 dark:tw-bg-gray-900 tw-p-4 tw-border tw-border-gray-200 dark:tw-border-gray-700">
                    <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 dark:tw-text-gray-400">
                      When
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-text-gray-900 dark:!tw-text-gray-100">
                      {formatKeyDate(
                        booking.keyDate,
                        booking?.guest?.guestTimezone
                      )}
                    </div>
                    {booking?.guest?.guestTimezone && (
                      <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                        Time zone: {booking.guest.guestTimezone}
                      </div>
                    )}
                  </div>

                  <div className="tw-rounded-xl tw-bg-gray-50 dark:tw-bg-gray-900 tw-p-4 tw-border tw-border-gray-200 dark:tw-border-gray-700">
                    <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 dark:tw-text-gray-400">
                      Location
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-text-gray-900 dark:!tw-text-gray-100">
                      {booking?.info?.location?.value || "—"}
                    </div>
                    {booking?.info?.location?.type && (
                      <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                        ({booking.info.location.type})
                      </div>
                    )}
                  </div>

                  <div className="tw-rounded-xl tw-bg-gray-50 dark:tw-bg-gray-900 tw-p-4 tw-border tw-border-gray-200 dark:tw-border-gray-700">
                    <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 dark:tw-text-gray-400">
                      Guest
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-text-gray-900 dark:!tw-text-gray-100">
                      {booking?.guest?.guestName || "—"}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                      {booking?.guest?.guestEmail || "—"}
                    </div>
                  </div>

                  <div className="tw-rounded-xl tw-bg-gray-50 dark:tw-bg-gray-900 tw-p-4 tw-border tw-border-gray-200 dark:tw-border-gray-700">
                    <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-500 dark:tw-text-gray-400">
                      Summary
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-text-gray-900 dark:!tw-text-gray-100">
                      You are about to{" "}
                      <span
                        className={
                          action === "accept"
                            ? "tw-text-green-600"
                            : "tw-text-red-600"
                        }
                      >
                        {action}
                      </span>{" "}
                      this booking.
                    </div>
                  </div>
                </div>

                <div className="tw-mt-2 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                  This action will notify the guest accordingly.
                </div>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-2">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="tw-px-4 tw-py-2 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-text-gray-700 dark:!tw-text-gray-300 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || (!!bookingError && !booking)}
              className={`tw-px-4 tw-py-2 tw-rounded tw-text-white disabled:tw-opacity-50 ${
                action === "accept"
                  ? "tw-bg-green-600 hover:tw-bg-green-700"
                  : "tw-bg-red-600 hover:tw-bg-red-700"
              }`}
            >
              {loading ? (
                <svg
                  className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="tw-opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="tw-opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"
                  />
                </svg>
              ) : (
                primaryCtaLabel
              )}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="tw-p-6 tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-shadow-md tw-w-full tw-max-w-lg">
          <h2
            className={`tw-text-lg tw-font-semibold ${
              result.success ? "tw-text-green-600" : "tw-text-red-600"
            }`}
          >
            {result.success ? "Success!" : "Error"}
          </h2>
          <p className="tw-mt-2 tw-text-gray-700 dark:!tw-text-gray-100">
            {result.message}
          </p>
          <div className="tw-mt-4 tw-flex tw-justify-end">
            <button
              onClick={() => history.push("/")}
              className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700"
            >
              Go Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
